/**
 * 1:1 port of packages/amgdragontiger_v2/lib/core/utils/result_parser.dart
 *
 * Input format: "suit-value,suit-value,dX,tX,wX"
 *   e.g. "clubs-5,hearts-10,d5,t10,w263"
 *
 * Output card numbers are 1..52 using the offsets in SUIT_OFFSET.
 */
import type { CardSuit, GameResult } from '../types/game';
import { BET_ITEM_ID, SUIT_OFFSET, gameResultFromWinningBetId } from '../types/game';

export interface ParsedResult {
  dragonCardNumber: number;
  tigerCardNumber: number;
  dragonPoint: number;
  tigerPoint: number;
  winningBetItemId: number;
  gameResult: GameResult;
}

export function parseResultString(raw: string): ParsedResult {
  const parts = raw
    .split(',')
    .map((p) => p.trim())
    .filter((p) => p.length > 0);

  if (parts.length < 4) {
    throw new Error(`Invalid result format: ${raw}`);
  }

  const dragonCardNumber = parseCardToNumber(parts[0]);
  const tigerCardNumber = parseCardToNumber(parts[1]);

  const dragonPoint = parsePoint(parts[2]);
  const tigerPoint = parsePoint(parts[3]);

  const winningBetItemId =
    parts.length >= 5
      ? parseWinningBetItemId(parts[4], dragonPoint, tigerPoint)
      : inferWinningBetItemId(dragonPoint, tigerPoint);

  return {
    dragonCardNumber,
    tigerCardNumber,
    dragonPoint,
    tigerPoint,
    winningBetItemId,
    gameResult: gameResultFromWinningBetId(winningBetItemId),
  };
}

function parsePoint(raw: string): number {
  const m = /\d+/.exec(raw);
  if (!m) throw new Error(`Invalid point format: ${raw}`);
  return parseInt(m[0], 10);
}

function parseWinningBetItemId(
  raw: string,
  dragonPoint: number,
  tigerPoint: number,
): number {
  const m = /\d+/.exec(raw);
  if (m) {
    const v = parseInt(m[0], 10);
    if (
      v === BET_ITEM_ID.dragon ||
      v === BET_ITEM_ID.tiger ||
      v === BET_ITEM_ID.tie
    ) {
      return v;
    }
  }
  return inferWinningBetItemId(dragonPoint, tigerPoint);
}

function inferWinningBetItemId(dragonPoint: number, tigerPoint: number): number {
  if (dragonPoint > tigerPoint) return BET_ITEM_ID.dragon;
  if (dragonPoint < tigerPoint) return BET_ITEM_ID.tiger;
  return BET_ITEM_ID.tie;
}

function parseCardToNumber(card: string): number {
  const parts = card.split('-');
  if (parts.length !== 2) throw new Error(`Invalid card format: ${card}`);
  const suit = parts[0].toLowerCase() as CardSuit;
  if (!(suit in SUIT_OFFSET)) throw new Error(`Invalid suit: ${suit}`);
  const value = parseInt(parts[1], 10);
  return SUIT_OFFSET[suit] + value;
}
